import type { ChartKey, HandClass, Position, PreflopChart, Situation } from '../types';
import { allHandClasses } from './rank';

/**
 * Heuristic preflop ranking (G1): Sklansky-Group-inspired tiers + a simple
 * "number of combos" rank so the top-N% cutoff feels poker-intuitive.
 *
 * This is NOT a GTO solver. It is a license-clean approximation that lets
 * the engine play coherent preflop poker. Real GTO charts (CC-licensed or
 * licensed) can replace this file in v2 without API churn.
 */

const RANKS = '23456789TJQKA';
const RANK_VALUE: Record<string, number> = {};
for (let i = 0; i < RANKS.length; i++) {
  RANK_VALUE[RANKS[i] as string] = i + 2; // 2..14 (Ace=14)
}

/** Numeric strength score used for tier sorting. */
export function strength(handClass: HandClass): number {
  if (handClass.length === 2) {
    const r = RANK_VALUE[handClass[0] as string] ?? 2;
    return 1000 + r * 100; // pocket pairs dominate
  }
  const hi = RANK_VALUE[handClass[0] as string] ?? 2;
  const lo = RANK_VALUE[handClass[1] as string] ?? 2;
  const gap = hi - lo;
  const suited = handClass[2] === 's';
  const connectorBonus = gap <= 4 ? (5 - gap) * 3 : 0;
  const suitedBonus = suited ? 15 : 0;
  const highCardBonus = hi * 8 + lo * 3;
  return highCardBonus + connectorBonus + suitedBonus;
}

const POSITION_OPEN_PCT: Record<Position, number> = {
  UTG: 0.14,
  HJ: 0.18,
  CO: 0.25,
  BTN: 0.45,
  SB: 0.35,
  BB: 0.0, // BB never "opens" — we track BB defense under vs-open.
};

const POSITION_VS_OPEN_PCT: Record<Position, number> = {
  UTG: 0.12,
  HJ: 0.15,
  CO: 0.2,
  BTN: 0.28,
  SB: 0.22,
  BB: 0.45, // BB defends wide vs open
};

const POSITION_3BET_PCT: Record<Position, number> = {
  UTG: 0.07,
  HJ: 0.08,
  CO: 0.09,
  BTN: 0.11,
  SB: 0.1,
  BB: 0.12,
};

const POSITION_VS_3BET_PCT: Record<Position, number> = {
  UTG: 0.03,
  HJ: 0.04,
  CO: 0.05,
  BTN: 0.06,
  SB: 0.04,
  BB: 0.05,
};

const POSITION_4BET_PCT: Record<Position, number> = {
  UTG: 0.02,
  HJ: 0.025,
  CO: 0.03,
  BTN: 0.035,
  SB: 0.03,
  BB: 0.035,
};

type PctTable = Record<Position, number>;

/**
 * Combos ("weight") each 169-class represents at a full 52-card table:
 *   pairs   → 6 combos
 *   suited  → 4 combos
 *   offsuit → 12 combos
 */
function combos(handClass: HandClass): number {
  if (handClass.length === 2) return 6;
  return handClass[2] === 's' ? 4 : 12;
}

const TOTAL_COMBOS = 52 * 51; // 2652 unordered = 1326, × 2 oriented... for pct math we use 1326

function buildChartFromCutoff(pct: number): PreflopChart {
  const ranked = allHandClasses().sort((a, b) => strength(b) - strength(a));
  const chart: Record<HandClass, number> = {};
  const targetCombos = pct * 1326;
  let accumulated = 0;
  for (const cls of ranked) {
    const c = combos(cls);
    if (accumulated >= targetCombos) {
      chart[cls] = 0;
      continue;
    }
    const remaining = targetCombos - accumulated;
    if (remaining >= c) {
      chart[cls] = 1;
      accumulated += c;
    } else {
      // Partial frequency for the cutoff edge
      chart[cls] = Number((remaining / c).toFixed(2));
      accumulated = targetCombos;
    }
  }
  // Ensure every class appears (explicit 0 if below threshold)
  for (const cls of ranked) {
    if (chart[cls] === undefined) chart[cls] = 0;
  }
  return chart;
}

function pctTableFor(situation: Situation): PctTable {
  switch (situation) {
    case 'open':
      return POSITION_OPEN_PCT;
    case 'vs-open':
      return POSITION_VS_OPEN_PCT;
    case '3bet':
      return POSITION_3BET_PCT;
    case 'vs-3bet':
      return POSITION_VS_3BET_PCT;
    case '4bet':
      return POSITION_4BET_PCT;
  }
}

const cache = new Map<string, PreflopChart>();

/**
 * Return the preflop chart for (position, situation). Cached.
 */
export function chartFor(key: ChartKey): PreflopChart {
  const cacheKey = `${key.position}:${key.situation}`;
  const cached = cache.get(cacheKey);
  if (cached) return cached;
  const pct = pctTableFor(key.situation)[key.position];
  const chart = buildChartFromCutoff(pct);
  cache.set(cacheKey, chart);
  return chart;
}

export { TOTAL_COMBOS };
